import React from 'react'
import {
  Users,
  Buildings,
  TrophyIcon,
  TargetIcon,
  PlantIcon,
  UsersIcon,
  UsersFourIcon,
  ReadCvLogoIcon,
  ClipboardText,
  Coins,
  UserList,
  ChatsCircleIcon,
  User,
  FileText,
  BookOpenIcon,
  GraduationCapIcon,
  Plug,
  CalendarIcon,
  HandshakeIcon,
  AddressBookIcon,
  IdentificationCard,
  House,
  SquaresFour,
  PresentationChart,
  Storefront,
  ChartBar,
  Database,
  NotePencil,
} from '@phosphor-icons/react'
import { RouteIcon, Coins as CoinsLucide, Settings2, TrendingUp } from 'lucide-react'

export interface NavItemConfig {
  id: string
  icon: (isActive: boolean) => React.ReactNode
  label: string
  path: string
  activePath?: string  // If set, used for active-state matching instead of path
  excludeActivePath?: string  // If current path starts with this, item is NOT active
  submoduleId?: string
  children?: NavItemConfig[]  // Support for nested items
  requiresRole?: 'admin' | 'learner' | 'any'  // Role-based visibility
  // Role-based access control
  requireHRManager?: boolean
  requireRCManager?: boolean
  requireRCEmployee?: boolean
  requireAnyManager?: boolean
  requireSuperAdmin?: boolean  // Only superadmin can see this item
  requireCoach?: boolean       // Coaches and coachees can see this item
  // Access matrix permissions - if user has ANY of these, they get access
  requireAnyPermission?: string[]
  // Customer module access (for customer employees/policyholders)
  // If set, only show if customer has this module in their access[] array
  customerModuleAccess?: 'LMS' | 'TMS' | 'Coaching'
  // Feature flag: only show if account has can_manage_customers enabled
  requireCanManageCustomers?: boolean
  // Subscription gate: only show if the specified module is enabled in org subscription.
  // Evaluated even for items inside ALWAYS_VISIBLE_MODULES sections.
  requireModuleEnabled?: string
  // Subscription gate: only show if ANY of the specified modules is enabled.
  // Evaluated even for items inside ALWAYS_VISIBLE_MODULES sections.
  requireAnyModuleEnabled?: string[]

  // Submodule-level subscription gate: only show if the specified submodule is enabled.
  // Evaluated even for items inside ALWAYS_VISIBLE_MODULES sections.
  requireSubmoduleEnabled?: { moduleId: string; submoduleId: string }
}

export interface NavSectionConfig {
  id: string
  title: string
  moduleId: string
  items: NavItemConfig[]
  defaultCollapsed?: boolean
}

/**
 * Master navigation configuration
 * This is the complete list of all possible navigation items
 * The actual rendered items will be filtered based on user subscriptions
 */
/**
 * Top-level navigation items that appear outside collapsible sections
 */
export const TOP_LEVEL_NAV_ITEMS: NavItemConfig[] = []

/**
 * Standalone navigation items (for standalone frontend mode)
 */
export const STANDALONE_NAV_ITEMS: NavItemConfig[] = []

export const NAVIGATION_CONFIG: NavSectionConfig[] = [
  {
    id: 'my-space',
    title: 'My Space',
    moduleId: 'my-space', // Always visible
    defaultCollapsed: false,
    items: [
      {
        id: 'home',
        icon: (isActive) => (
          <House
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Home',
        path: '/home/my-portal',
        activePath: '/home',
      },
      {
        id: 'my-profile',
        icon: (isActive) => (
          <User
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'My Profile',
        path: '/my-profile',
      },
    ],
  },
  {
    id: 'company',
    title: 'TraineryCore',
    moduleId: 'company', // This is a special module that's always enabled
    defaultCollapsed: false,
    items: [
      {
        id: 'workforce',
        icon: (isActive) => (
          <Users
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Workforce',
        path: '/workforce/overview',
        activePath: '/workforce',
        // Visible to everyone - tabs inside control per-feature access
        // Customer admins see People tab inside Workforce (handled by WorkforceTabs.tsx)
      },
      {
        id: 'organisation',
        icon: (isActive) => (
          <Buildings
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Organization',
        path: '/organization',
        requireSuperAdmin: true,  // Only superadmin can access Organization
      },
      {
        id: 'customers',
        icon: (isActive) => (
          <UserList
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Customers',
        path: '/customers',
        requireHRManager: true,
        requireCanManageCustomers: true,
      },
      {
        id: 'integrations',
        icon: (isActive) => (
          <Plug
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Integrations',
        path: '/integrations/overview',
        activePath: '/integrations',
        requireSuperAdmin: true,
      },
      {
        id: 'analytics',
        icon: (isActive) => (
          <ChartBar
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Analytics',
        path: '/analytics',
        requireAnyModuleEnabled: ['review-cloud', 'learn'],
        children: [
          {
            id: 'dashboards',
            icon: (isActive) => (
              <SquaresFour
                weight={isActive ? 'fill' : 'bold'}
                color={isActive ? '#4B45E5' : '#6B7280'}
                className="w-5 h-5"
              />
            ),
            label: 'Dashboards',
            path: '/analytics/dashboards',
          },
          {
            id: 'reports-config',
            icon: (isActive) => (
              <FileText
                weight={isActive ? 'fill' : 'bold'}
                color={isActive ? '#4B45E5' : '#6B7280'}
                className="w-5 h-5"
              />
            ),
            label: 'Reports',
            path: '/analytics/reports/perform-spark',
            activePath: '/analytics/reports',
          },
          {
            id: 'report-builder',
            icon: (isActive) => (
              <PresentationChart
                weight={isActive ? 'fill' : 'bold'}
                color={isActive ? '#4B45E5' : '#6B7280'}
                className="w-5 h-5"
              />
            ),
            label: 'Custom Reports',
            path: '/analytics/report-builder',
          },
        ],
      },
    ],
  },
  {
    id: 'perform-spark',
    title: 'PerformSpark',
    moduleId: 'perform-spark',
    defaultCollapsed: false,
    items: [
      {
        id: 'cycles',
        submoduleId: 'cycle-management',
        icon: (isActive) => (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5"
          >
            <path
              d="M16.24 3.5 A 9 9 0 1 1 7.76 3.5"
              stroke={isActive ? '#4B45E5' : '#6B7280'}
              strokeWidth={isActive ? 3 : 2.5}
              strokeLinecap="round"
            />
          </svg>
        ),
        label: 'Cycles',
        path: '/performspark/cycle-management',
        // STRICT RBAC - HRBP only (requires CREATE/EDIT permission)
        // NO role bypasses - all access from permissions only
        requireAnyPermission: [
          'PERFORMANCE_CYCLES_EDIT',
          'PERFORMANCE_CYCLES_CREATE',
        ],
      },
      {
        id: 'performance',
        submoduleId: 'performance-management',
        icon: (isActive) => (
          <TrophyIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Performance',
        path: '/performspark/performance',
      },
      {
        id: 'goals',
        submoduleId: 'goal-management',
        icon: (isActive) => (
          <TargetIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Goals',
        path: '/performspark/goal-management',
      },
      {
        id: 'talent',
        submoduleId: 'talent-management',
        icon: (isActive) => (
          <PlantIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Talent',
        path: '/performspark/talent-management',
      },
      {
        id: 'surveys',
        submoduleId: 'survey',
        icon: (isActive) => (
          <ChatsCircleIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Surveys',
        path: '/performspark/surveys',
      },
      // recognition is its own top-level subscription module (module_id: 'recognition')
      // surfaced here under PerformSpark; gated by requireModuleEnabled, not submoduleId
      {
        id: 'recognitions',
        requireModuleEnabled: 'recognition',
        icon: (isActive) => (
          <HandshakeIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Recognitions',
        path: '/my-space/recognitions',
      },

    ],
  },
  {
    id: 'comp-builder',
    title: 'CompBldr',
    moduleId: 'comp-builder',
    defaultCollapsed: false,
    items: [
      {
        id: 'job-builder',
        submoduleId: 'job-bldr',
        icon: (isActive) => (
          <ReadCvLogoIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'JobBldr',
        path: '/comp-builder/job-builder',
      },
      {
        id: 'job-architecture',
        submoduleId: 'job-architecture',
        icon: (isActive) => (
          <UsersFourIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Job Architecture',
        path: '/comp-builder/job-architecture',
      },
      {
        id: 'Position Manager',
        submoduleId: 'position-manager',
        icon: (isActive) => (
          <AddressBookIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Position Manager',
        path: '/comp-builder/position-manager',
      },
      {
        id: 'job-evaluation',
        submoduleId: 'job-evaluation',
        icon: (isActive) => (
          <ClipboardText
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Job Evaluation',
        path: '/comp-builder/job-evaluation',
      },
      {
        id: 'pay-builder',
        submoduleId: 'pay-bldr',
        icon: (isActive) => (
          <Coins
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Comp Planner',
        path: '/comp-builder/pay-builder',
        children: [
          {
            id: 'comp-planner-cycles',
            icon: (isActive) => (
              <CalendarIcon
                weight={isActive ? 'fill' : 'bold'}
                color={isActive ? '#4B45E5' : '#6B7280'}
                className="w-5 h-5"
              />
            ),
            label: 'Cycles',
            path: '/comp-builder/pay-builder/cycles',
            activePath: '/comp-builder/pay-builder/cycles',
            requireSuperAdmin: true,
          },
          {
            id: 'comp-planner-planning',
            icon: (isActive) => (
              <SquaresFour
                weight={isActive ? 'fill' : 'bold'}
                color={isActive ? '#4B45E5' : '#6B7280'}
                className="w-5 h-5"
              />
            ),
            label: 'My Workspace',
            path: '/comp-builder/pay-builder',
            activePath: '/comp-builder/pay-builder',
            excludeActivePath: '/comp-builder/pay-builder/cycles',
          },
        ],
      },
      {
        id: 'compensation',
        submoduleId: 'pay-bldr',
        icon: (isActive) => (
          <IdentificationCard
            weight={isActive ? 'fill' : 'regular'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Compensation',
        path: '/comp-builder/compensation',
      },
      {
        id: 'total-rewards',
        submoduleId: 'total-rewards',
        icon: (isActive) => (
          <FileText
            weight={isActive ? 'fill' : 'regular'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Total Rewards',
        path: '/comp-builder/stmt-builder',
      },
      {
        id: 'data-bldr',
        submoduleId: 'market-bldr',
        icon: (isActive) => (
          <Database
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'DataBldr',
        path: '/comp-builder/data-bldr',
      },
      {
        id: 'market-pricing',
        submoduleId: 'market-bldr',
        icon: (isActive) => (
          <TrendingUp
            strokeWidth={isActive ? 3 : 2.5}
            height={20}
            width={20}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Benchmarking',
        path: '/comp-builder/market-pricing',
      },
      {
        id: 'templates',
        requireAnyModuleEnabled: ['comp-builder'],
        icon: (isActive) => (
          <NotePencil
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Templates',
        path: '/comp-builder/templates',
      },
    ],
  },
  {
    id: 'learn',
    title: 'TraineryLearn',
    moduleId: 'learn',
    defaultCollapsed: false,
    items: [
      {
        id: 'courses',
        icon: (isActive) => (
          <BookOpenIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Courses',
        path: '/learn/courses',
        // RBAC: Only show to users who can manage courses (not learners)
        requireAnyPermission: ['COURSES_CREATE', 'COURSES_EDIT', 'LEARNING_JOURNEY_VIEW'],
      },
      {
        id: 'marketplace',
        icon: (isActive) => (
          <Storefront
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Trainery Marketplace',
        path: '/learn/marketplace',
        activePath: '/learn/marketplace',
      },
      {
        id: 'learning-journey',
        icon: (isActive) => (
          <RouteIcon
            strokeWidth={3}
            height={20}
            width={20}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Learning Journey',
        path: '/learn/learning-journey',
      },
      {
        id: 'learning-operations',
        icon: (isActive) => (
          <Settings2
            strokeWidth={isActive ? 3 : 2.5}
            height={20}
            width={20}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'TraineryOps',
        path: '/learn/learning-operations',
        requireAnyPermission: [
          'ASSIGNMENTS_VIEW',
          'COURSES_EDIT',
          'COURSES_CREATE',
        ],
      },
      {
        id: 'training-management',
        submoduleId: 'training-management',
        icon: (isActive) => (
          <UsersFourIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Training Management',
        path: '/learn/track',
        requireAnyPermission: ['TMS_EVENTS_VIEW'],
      },
      {
        id: 'coaching',
        submoduleId: 'coaching',
        icon: (isActive) => (
          <UsersIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'Trainery CMS',
        path: '/learn/coaching/dashboard',
        activePath: '/learn/coaching',
        requireAnyPermission: ['COACHING_ENGAGEMENTS_VIEW', 'COACHING_COACHES_VIEW'],
        requireCoach: true, // Coaches and coachees also see this
      },
      {
        id: 'e-learning',
        submoduleId: 'lms',
        icon: (isActive) => (
          <GraduationCapIcon
            weight={isActive ? 'fill' : 'bold'}
            color={isActive ? '#4B45E5' : '#6B7280'}
            className="w-5 h-5"
          />
        ),
        label: 'LMS',
        path: '/learn/e-learning',
        // RBAC: Show if user has any LMS management permissions
        requireAnyPermission: [
          'COURSES_CREATE',
          'COURSES_EDIT',
          'ASSIGNMENTS_VIEW',
          'VIEW_POOLS_VIEW',
        ],
        customerModuleAccess: 'LMS', // Customer employees need LMS access
      },
    ],
  },
]

/**
 * Modules that are always visible regardless of subscription
 */
export const ALWAYS_VISIBLE_MODULES = ['my-space', 'company', 'analytics']
